import React, { useMemo } from 'react';
import Icon from '../../../components/AppIcon';

// Activity Icon mapping
const getActivityIcon = (type) => {
  switch (type) {
    case 'application_received':
      return { name: 'FileText', color: 'bg-blue-100 text-blue-600' };
    case 'job_posted':
      return { name: 'Briefcase', color: 'bg-primary-100 text-primary' };
    case 'message_sent':
      return { name: 'MessageSquare', color: 'bg-purple-100 text-purple-600' };
    case 'interview_scheduled':
      return { name: 'Calendar', color: 'bg-success-100 text-success' };
    case 'contract_started':
      return { name: 'UserCheck', color: 'bg-warning-100 text-warning' };
    case 'contract_ended':
      return { name: 'UserX', color: 'bg-error-100 text-error' };
    case 'system_notification':
      return { name: 'Bell', color: 'bg-secondary-100 text-secondary-700' };
    case 'candidate_viewed':
      return { name: 'Eye', color: 'bg-teal-100 text-teal-600' };
    case 'job_status_changed':
      return { name: 'RefreshCw', color: 'bg-indigo-100 text-indigo-600' };
    default:
      return { name: 'Activity', color: 'bg-gray-100 text-gray-600' };
  }
};

// Format time
const formatTime = (timestamp) => {
  return new Date(timestamp).toLocaleTimeString('en-US', {
    hour: '2-digit',
    minute: '2-digit'
  });
};

const ActivityItem = React.memo(({ activity, onActivityClick }) => {
  const icon = getActivityIcon(activity.activity_type);
  
  return (
    <div 
      className="relative mb-5 last:mb-0"
      onClick={() => onActivityClick(activity)}
    >
      {/* Timeline dot */}
      <div className="absolute -left-[10px] top-3 w-4 h-4 rounded-full bg-white border-2 border-secondary-200"></div>
      
      {/* Activity card */}
      <div className="ml-6 bg-white p-4 rounded-lg border border-border hover:shadow-md transition-shadow cursor-pointer">
        <div className="flex items-start">
          {/* Icon */}
          <div className={`w-10 h-10 rounded-lg ${icon.color} flex items-center justify-center mr-4`}>
            <Icon name={icon.name} size={18} />
          </div>
          
          {/* Content */}
          <div className="flex-1">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-2">
              <h5 className="font-medium text-text-primary">
                {activity.activity_type.split('_').map(word => 
                  word.charAt(0).toUpperCase() + word.slice(1)).join(' ')}
              </h5>
              <span className="text-xs text-text-secondary">{formatTime(activity.activity_date)}</span>
            </div>
            <p className="text-sm text-text-secondary mb-2">{activity.description}</p>
            
            {/* Additional metadata */}
            {activity.metadata && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-3">
                {activity.metadata.job_title && (
                  <div className="flex items-center text-xs">
                    <Icon name="Briefcase" size={14} className="text-text-muted mr-1" />
                    <span className="text-text-secondary">{activity.metadata.job_title}</span>
                  </div>
                )}
                {(activity.metadata.va_name || activity.metadata.candidate_name || activity.metadata.applicant_name) && (
                  <div className="flex items-center text-xs">
                    <Icon name="User" size={14} className="text-text-muted mr-1" />
                    <span className="text-text-secondary">
                      {activity.metadata.va_name || activity.metadata.candidate_name || activity.metadata.applicant_name}
                    </span>
                  </div>
                )}
                {activity.metadata.status && (
                  <div className="flex items-center text-xs">
                    <Icon name="Tag" size={14} className="text-text-muted mr-1" />
                    <span className="text-text-secondary capitalize">{activity.metadata.status}</span>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
});

const DateGroup = React.memo(({ date, activities, onActivityClick }) => {
  return (
    <div className="mb-8 last:mb-0">
      <div className="flex items-center mb-4">
        <div className="w-10 h-10 rounded-full bg-secondary-50 flex items-center justify-center mr-3">
          <Icon name="Calendar" size={16} className="text-text-secondary" />
        </div>
        <h4 className="text-md font-medium text-text-primary">{date}</h4>
      </div>

      <div className="pl-5 border-l-2 border-secondary-200">
        {activities.map((activity, index) => (
          <ActivityItem 
            key={activity.id || index} 
            activity={activity} 
            onActivityClick={onActivityClick} 
          />
        ))}
      </div>
    </div>
  );
});

const ActivityTimeline = ({ activities, onActivityClick }) => {
  // Group activities by date and memoize the result
  const { groupedActivities, sortedDates } = useMemo(() => {
    const grouped = activities.reduce((groups, activity) => {
      const date = new Date(activity.activity_date).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
      
      if (!groups[date]) {
        groups[date] = [];
      }
      
      groups[date].push(activity);
      return groups;
    }, {});

    // Sort dates in descending order
    const sorted = Object.keys(grouped).sort((a, b) => {
      return new Date(b) - new Date(a);
    });

    return { groupedActivities: grouped, sortedDates: sorted };
  }, [activities]);

  return (
    <div className="bg-surface rounded-lg border border-border">
      <div className="p-4 border-b border-border">
        <h3 className="font-semibold text-text-primary">Activity Timeline</h3>
        <p className="text-sm text-text-secondary mt-1">
          Showing {activities.length} {activities.length === 1 ? 'activity' : 'activities'}
        </p>
      </div>

      <div className="p-4">
        {sortedDates.map((date) => (
          <DateGroup 
            key={date} 
            date={date} 
            activities={groupedActivities[date]} 
            onActivityClick={onActivityClick} 
          />
        ))}
      </div>
    </div>
  );
};

export default ActivityTimeline;

